import React, { useState, useEffect, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import { icons } from 'seal-ui-components';
import store from '@Store';
import { NOTIFICATION_HANDLER, MIX_PANEL } from '@Constants/types';
import { removeExtDuplications } from '@Utils/arrayUtils';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import ScoreCardAnalytic from './components/ScoreCardAnalytic';
import mixpanelEvents from '@Mixpanel';
import {
    ScoreCard,
    TopDiv,
    RiskDiv,
    TooltipDiv,
    InfoDiv,
    AnswerDiv,
    QuestionDiv,
    ArrowsDiv,
    RiskLabel,
    ClauseIconWrapper,
    BlackLabel,
    GrayLabel,
    ExtLabel,
    CollapsableDiv
} from './ScoreCardItem.style';
import { AskQuestionIcon, ListIcon } from '@Components/UI/Icons';

const NO_EXTRACTION = 'No extraction';

const ScoreCardItem = ({
    riskColor,
    riskLevel,
    answerField,
    question,
    topic,
    subtopic,
    logicDescription,
    ruleId,
    analytics,
    analyticsSummary,
    suggestedClauseGroupsMap,
    uid,
    rulePosition,
    // eslint-disable-next-line no-empty-function
    openDetails = () => {}
}) => {
    const [collapsed, setCollapsed] = useState(true);
    const [curExtIndex, setCurExtIndex] = useState(-1);
    const [curExtNumber, setCurExtNumber] = useState(0);
    const [numberOfExtractions, setNumberOfExtractions] = useState(0);
    const [filteredAnalytics, setFilteredAnalytics] = useState([]);
    const [highlightedLocationName, setHighlightedLocationName] = useState('');
    const [highlightedLocationIdx, setHighlightedLocationIdx] = useState('');

    useEffect(
        () => {
            if (analyticsSummary) {
                filteredAnalyticsValues(
                    Array.isArray(analyticsSummary) ? analyticsSummary : [analyticsSummary]
                );
            } else {
                filteredAnalyticsValues(analytics);
            }
        },
        [analytics, analyticsSummary]
    );

    function filteredAnalyticsValues(values) {
        const filteredAnalyticsByExt = values.filter(
            analytic => analytic.values[0].value && analytic.values[0].value !== NO_EXTRACTION
        );

        const filteredAnalyticsByValues = removeExtDuplications(filteredAnalyticsByExt).filter(
            analytic => analytic.values.length
        );

        const analyticsValues = filteredAnalyticsByValues
            .reduce((acc, curVal) => acc.concat(curVal.values), [])
            .sort((a, b) => a.startOffset - b.startOffset);

        const valuesLength = analyticsValues.length;
        if (valuesLength) {
            setFilteredAnalytics(analyticsValues);
            setNumberOfExtractions(valuesLength);
        }
    }

    const onMouseEnterInfoHandler = () => {
        const mixpanel = {
            riskLevel,
            rulePosition,
            extractionCount: filteredAnalytics.length
        };
        store.dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_SCORECARD,
            payload: mixpanel
        });

        mixpanelEvents.ruleCardHoverOnDescriptionForTooltip();
    };

    const onMouseEnterQuestionHandler = () => {
        const mixpanel = {
            riskLevel,
            rulePosition,
            extractionCount: filteredAnalytics.length
        };
        store.dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_SCORECARD,
            payload: mixpanel
        });

        mixpanelEvents.ruleCardHoverOnQuestionForTooltip();
    };

    const policyHighlightHandler = (name, idx) => {
        try {
            setHighlightedLocationName(name);
            setHighlightedLocationIdx(idx);
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ScoreCardContainer.policyHighlightHandler', err);
            store.dispatch({ type: NOTIFICATION_HANDLER.ERROR, payload: err });
        }
    };

    const expand = () => {
        try {
            const mixpanel = {
                riskLevel,
                ruleId,
                rulePosition
            };

            store.dispatch({
                type: MIX_PANEL.MIXPANEL_DATA_SCORECARD,
                payload: mixpanel
            });

            if (collapsed) {
                mixpanelEvents.rulecardexpanded();
            } else {
                mixpanelEvents.rulecardcollapsed();
            }

            if (answerField.values[0].value !== 'No Analytics Hit') {
                setCollapsed(!collapsed);
            }
            setCurExtIndex(-1);
            setCurExtNumber(0);
            setHighlightedLocationName('');
            setHighlightedLocationIdx('');
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ScoreCardItem.expand', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    const openDetailsDrawer = e => {
        e.preventDefault();
        e.stopPropagation();
        openDetails({
            riskColor,
            riskLevel,
            answerField,
            question,
            topic,
            subtopic,
            logicDescription,
            ruleId,
            analytics,
            analyticsSummary,
            suggestedClauseGroupsMap,
            uid
        });

        const suggestedClauseGroupsCount = Object.keys(suggestedClauseGroupsMap).length;
        const mixpanel = {
            riskLevel,
            extractionCount: filteredAnalytics.length,
            ruleId,
            suggestedClauseGroupsCount
        };

        store.dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_SCORECARD,
            payload: mixpanel
        });
        mixpanelEvents.showLinkedClauses();
    };

    const arrowsHandler = (e, arrow) => {
        e.stopPropagation();
        const extractionsLength = filteredAnalytics.length - 1;

        const mixpanel = {
            riskLevel,
            rulePosition,
            extractionCount: filteredAnalytics.length
        };
        store.dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_SCORECARD,
            payload: mixpanel
        });

        mixpanelEvents.ruleCardExtractionsCycleKeyClicked();

        if (arrow === 'next') {
            setCurExtIndex(curExtIndex >= extractionsLength ? 0 : curExtIndex + 1);
            setCurExtNumber(curExtNumber >= numberOfExtractions ? 1 : curExtNumber + 1);
        }

        if (arrow === 'prev') {
            setCurExtIndex(curExtIndex <= 0 ? extractionsLength : curExtIndex - 1);
            setCurExtNumber(
                curExtNumber === 1 || curExtNumber === 0 ? numberOfExtractions : curExtNumber - 1
            );
        }
    };

    /** The scorecardIndex argument is not needed for this function to work,
     * it is only needed for testing purposes
     */
    const renderNavigationArrows = scorecardIndex => {
        if (!collapsed && numberOfExtractions) {
            return (
                <>
                    <ArrowsDiv>
                        <icons.ChevronUpSmallIcon
                            data-testid={`scorecard_${scorecardIndex}_extraction_arrow_up`}
                            onClick={e => arrowsHandler(e, 'prev')}
                        />
                        <icons.ChevronDownSmallIcon
                            data-testid={`scorecard_${scorecardIndex}_extraction_arrow_down`}
                            onClick={e => arrowsHandler(e, 'next')}
                        />
                    </ArrowsDiv>
                    <ExtLabel data-testid={`scorecard_${scorecardIndex}_total_extractions`}>
                        {curExtNumber}/{numberOfExtractions}
                    </ExtLabel>
                </>
            );
        }

        return numberOfExtractions ? (
            <ExtLabel data-testid={`scorecard_${scorecardIndex}_total_extractions`}>
                {numberOfExtractions}
            </ExtLabel>
        ) : null;
    };

    const highlightObj = useMemo(
        () => {
            const highlight = {
                highlightedLocationName,
                highlightedLocationIdx,
                policyHighlightHandler
            };
            return highlight;
        },

        [highlightedLocationName, highlightedLocationIdx]
    );

    return (
        <ScoreCard
            id={`${topic}_${uid}`}
            key={`${topic}_${uid}`}
            riskColor={riskColor}
            data-testid={`scorecard_${uid}`}
        >
            <TopDiv
                data-testid={`scorecard_${uid}_expand_analytic`}
                onClick={filteredAnalytics.length > 0 ? () => expand() : null}
            >
                <RiskDiv>
                    <RiskLabel
                        data-tip={riskLevel && riskLevel.length > 7 ? `${riskLevel}` : ''}
                        data-class="tooltip"
                        style={{ color: riskColor }}
                        data-testid={`scorecard_${uid}_risk`}
                        data-for={`riskTooltip-${uid}`}
                    >
                        {riskLevel}
                    </RiskLabel>
                    <ReactTooltip id={`riskTooltip-${uid}`} />
                </RiskDiv>
                <InfoDiv>
                    <div id="info-div">
                        <GrayLabel data-testid={`scorecard_${uid}_topic`}>{topic}</GrayLabel>
                        <div data-testid={`scorecard_${uid}_subtopic_icon_wrapper`}>
                            <icons.InfoSmallIcon
                                data-testid={`scorecard_${uid}_subtopic_icon`}
                                onMouseEnter={onMouseEnterInfoHandler}
                            />

                            <TooltipDiv
                                index={uid}
                                data-testid={`scorecard_${uid}_subtopic_tooltip`}
                            >
                                <p className="tooltip-header">SUB-TOPIC RULE</p>
                                <p className="tooltip-body">{logicDescription}</p>
                            </TooltipDiv>
                        </div>
                    </div>
                    <BlackLabel data-testid={`scorecard_${uid}_subtopic`}>{subtopic}</BlackLabel>
                    {suggestedClauseGroupsMap && (
                        <ClauseIconWrapper
                            isCollapsed={collapsed}
                            onClick={e => openDetailsDrawer(e)}
                            data-testid={`scorecard_${uid}_clause_icon_wrapper`}
                        >
                            <ListIcon />
                        </ClauseIconWrapper>
                    )}
                    <AnswerDiv>
                        <QuestionDiv data-testid={`scorecard_${uid}_question_icon_wrapper`}>
                            <AskQuestionIcon
                                data-testid={`scorecard_${uid}_question_icon`}
                                onMouseEnter={onMouseEnterQuestionHandler}
                                style={{ marginRight: '5px' }}
                            />
                            <TooltipDiv uid={uid} data-testid={`scorecard_${uid}_question_tooltip`}>
                                <p className="tooltip-header">QUESTION</p>
                                <p className="tooltip-body">{question}</p>
                            </TooltipDiv>
                        </QuestionDiv>
                        <GrayLabel data-testid={`scorecard_${uid}_answer_field`}>
                            {answerField.values[0].value}
                        </GrayLabel>
                        <div id="arrows">{renderNavigationArrows(uid)}</div>
                    </AnswerDiv>
                </InfoDiv>
            </TopDiv>

            {!collapsed &&
                filteredAnalytics.length > 0 && (
                    <CollapsableDiv>
                        <div>
                            {filteredAnalytics.map((filteredAnalytic, i) => (
                                <ScoreCardAnalytic
                                    key={`${uid}_${i}`}
                                    filteredAnalytic={filteredAnalytic}
                                    highlightObj={highlightObj}
                                    scorecardIndex={uid}
                                    extractionIndex={i}
                                    filteredAnalytics={filteredAnalytics}
                                    curExtIndex={curExtIndex}
                                    setCurExtIndex={setCurExtIndex}
                                    setCurExtNumber={setCurExtNumber}
                                    policyHighlightHandler={policyHighlightHandler}
                                    riskLevel={riskLevel}
                                    extractionCount={filteredAnalytics.length}
                                    ruleId={ruleId}
                                    rulePosition={rulePosition}
                                />
                            ))}
                        </div>
                    </CollapsableDiv>
                )}
        </ScoreCard>
    );
};

export default React.memo(ScoreCardItem, (prevProps, nextProps) => {
    if (JSON.stringify(prevProps) !== JSON.stringify(nextProps)) return false;

    return true;
});
